import type { LocationStatusFilter } from '@/shared/constants/location'
import { useTranslation } from 'react-i18next'
import { CountryListTable, LocationBannerDialog, ProvinceListTable } from '@/features/location'
import { TenantSearchBar } from '@/features/tenant/tenant-search-bar'
import { LOCATION_STATUS_FILTER } from '@/shared/constants/location'
import { MediaPreviewDialog } from '@/shared/ui/media-preview-dialog'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { useLocationBanner } from '@/pages/location/use-location-banner'
import { useLocationPage } from '@/pages/location/use-location-page'

/**
 * Read-only location management: countries and the provinces of the selected country.
 * Data comes from master-service.
 */
export function LocationListPage() {
  const { t } = useTranslation()
  const page = useLocationPage()
  const banner = useLocationBanner()
  const bannerTarget = banner.target

  return (
    <PageContainer>
      <PageHeader
        title={t('sidebar.locations')}
        subtitle={t('location.subtitle')}
        breadcrumbs={[{ label: t('sidebar.locations') }]}
      />

      <h2 className="mb-3 text-sm font-semibold text-zinc-700">{t('location.countries')}</h2>
      <CountryListTable
        countries={page.countries}
        loading={page.countriesLoading}
        selectedCountryId={page.selectedCountry?.id ?? ''}
        onSelect={page.selectCountry}
        onEditBanner={banner.editCountry}
        onPreviewBanner={banner.openPreview}
      />

      <h2 className="mb-3 mt-8 text-sm font-semibold text-zinc-700">
        {t('location.provincesOf', { country: page.selectedCountry?.nativeName ?? '' })}
      </h2>
      <DataTableToolbar>
        <DataTableFilterField label={t('common.search')} className="min-w-[240px]">
          <TenantSearchBar
            onSearch={page.changeKeyword}
            placeholder={t('location.searchPlaceholder')}
            className="max-w-none"
          />
        </DataTableFilterField>
        <DataTableFilterField label={t('common.status')}>
          <Select
            value={page.status}
            onValueChange={(value) => page.changeStatus(value as LocationStatusFilter)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('common.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={LOCATION_STATUS_FILTER.ALL}>{t('common.all')}</SelectItem>
              <SelectItem value={LOCATION_STATUS_FILTER.ACTIVE}>{t('common.active')}</SelectItem>
              <SelectItem value={LOCATION_STATUS_FILTER.INACTIVE}>
                {t('common.inactive')}
              </SelectItem>
            </SelectContent>
          </Select>
        </DataTableFilterField>
      </DataTableToolbar>
      <ProvinceListTable
        provinces={page.provinces}
        loading={page.provincesLoading}
        onEditBanner={banner.editProvince}
        onPreviewBanner={banner.openPreview}
        pagination={{
          current: page.provincePage,
          pageSize: page.provincePageSize,
          total: page.provinceTotal,
          onChange: page.changeProvincePage,
        }}
      />

      {bannerTarget ? (
        <LocationBannerDialog
          // * VI: key theo id để mỗi địa điểm có draft riêng | EN: keyed by id so every location gets a fresh draft
          key={bannerTarget.kind === 'country' ? bannerTarget.country.id : bannerTarget.province.id}
          locationName={
            bannerTarget.kind === 'country'
              ? bannerTarget.country.nativeName
              : bannerTarget.province.nativeName
          }
          initialBanner={
            bannerTarget.kind === 'country'
              ? bannerTarget.country.banner
              : bannerTarget.province.banner
          }
          saving={banner.saving}
          onSave={banner.save}
          onClose={banner.close}
          onPreview={banner.openPreview}
        />
      ) : null}
      <MediaPreviewDialog url={banner.previewUrl} onClose={banner.closePreview} />
    </PageContainer>
  )
}
